// FE-SCRM-051 — React Query hooks for the Add Opportunity modal.
//
// The create mutation invalidates the Renewal Board query so the
// new row surfaces in its cell without a refresh. The host modal
// also navigates to the opportunity detail on success.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { fetchBrokerLocations, fetchTeamMembers } from "@/features/settings/api";
import type {
  BrokerLocationListResponse,
  TeamMemberListResponse,
} from "@/features/settings/types";

import { RENEWAL_BOARD_QUERY_KEY } from "../RenewalBoard/hooks";
import { fetchGlobalSearch } from "../search/api";
import type { SearchResultRow } from "../search/types";
import type { TargetSearchResult } from "../components";

import {
  createOpportunity,
  fetchBrokerFirmsSuggestions,
  fetchBrokerOfficesSuggestions,
} from "./api";
import type { CreateOpportunityInput, CreatedOpportunity } from "./types";


export const TEAM_MEMBERS_QUERY_KEY = "team-members" as const;
export const BROKER_LOCATIONS_QUERY_KEY = "broker-locations" as const;


export function useTeamMembersQuery(): UseQueryResult<TeamMemberListResponse> {
  return useQuery({
    queryKey: [TEAM_MEMBERS_QUERY_KEY],
    queryFn: ({ signal }) => fetchTeamMembers({ signal }),
  });
}


export function useBrokerLocationsQuery():
  UseQueryResult<BrokerLocationListResponse>
{
  return useQuery({
    queryKey: [BROKER_LOCATIONS_QUERY_KEY],
    queryFn: ({ signal }) => fetchBrokerLocations({ signal }),
  });
}


export function useCreateOpportunityMutation(): UseMutationResult<
  CreatedOpportunity, Error, CreateOpportunityInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createOpportunity,
    onSuccess: () => {
      // Optimistic-ish: refetch all Renewal Board queries so the new
      // row surfaces in its (month × stage) cell without a refresh.
      // The board response is cheap (one composite SQL) and the
      // producer just submitted, so an extra round-trip is fine.
      qc.invalidateQueries({ queryKey: [RENEWAL_BOARD_QUERY_KEY] });
    },
  });
}


/** TargetSearchPicker fetcher for the prospect picker. Adapts the
 * FE-SCRM-054 ``/api/v1/sales/search`` payload into the picker's
 * ``TargetSearchResult`` shape. Scoped to prospects via the
 * ``restrict_to`` parameter — but the BE endpoint always returns
 * all five buckets, so we filter client-side. */
export async function searchProspectsForPicker(
  q: string,
): Promise<TargetSearchResult[]> {
  if (!q.trim()) return [];
  const res = await fetchGlobalSearch(q, { limit: 5 });
  return res.prospects.map((row: SearchResultRow) => ({
    id: row.id,
    kind: "prospect" as const,
    label: row.label,
    sublabel: row.sublabel ?? undefined,
  }));
}


export {
  fetchBrokerFirmsSuggestions,
  fetchBrokerOfficesSuggestions,
};
